import type { BlobLike } from "@pdfking/compatibility"
import type { ExtractPageResult } from "@pdfking/pdfium"
import { BlobArray } from "@pdfking/compatibility"
import { SortBy } from "@pdfking/tfunc"

export const PDF_HEADER =
  "%PDF-1.5\r\n1 0 obj <</Type/Catalog/Pages 2 0 R>> endobj\r\n"
const CATALOG_OFFSET = 10n

type RecomposeInput = {
  readonly extract: ExtractPageResult
  readonly order: number
}

type PageObjEntry = {
  readonly page_obj_num: bigint
  readonly order: number
}

const formatXrefEntry = (offset: bigint): string =>
  `${String(offset).padStart(10, "0")} 00000 n\r\n`

export async function* Recompose(
  pages: AsyncIterable<RecomposeInput> | Iterable<RecomposeInput>
): AsyncGenerator<BlobLike> {
  yield BlobArray(PDF_HEADER)

  let lastObjNum = 2n
  let documentSize = BigInt(PDF_HEADER.length)
  const pageEntries: PageObjEntry[] = []
  const xrefChunks: Uint8Array[] = []

  for await (const {
    extract: { content, xref, page_obj_num, highest_obj_num, bytes_written },
    order,
  } of pages) {
    if (content.length > 0) {
      yield BlobArray(content)
    }

    if (xref.length > 0) {
      xrefChunks.push(xref)
    }

    pageEntries.push({ page_obj_num, order })
    lastObjNum = highest_obj_num
    documentSize += bytes_written
  }

  if (pageEntries.length > 0) {
    const pagesOffset = documentSize
    const sorted = SortBy(pageEntries, e => e.order)
    const kids = sorted.map(e => `${e.page_obj_num} 0 R`).join(" ")
    const pagesObj = `2 0 obj <</Type/Pages/Count ${sorted.length}/Kids[${kids}]>> endobj\r\n`

    yield BlobArray([
      pagesObj,
      "xref\r\n",
      "0 1\r\n0000000000 65535 f\r\n",
      `1 ${lastObjNum}\r\n`,
      formatXrefEntry(CATALOG_OFFSET),
      formatXrefEntry(pagesOffset),
    ])

    for (const chunk of xrefChunks) {
      yield BlobArray(chunk)
    }

    yield BlobArray([
      `trailer\r\n<</Size ${lastObjNum + 1n}/Root 1 0 R>>\r\n`,
      `startxref\r\n${documentSize + BigInt(pagesObj.length)}\r\n%%EOF\r\n`,
    ])
  }
}
